/**
 * CommunityLocalLifeSection - Special section for Community & Local Life parent category
 * Shows subcategories, featured listings from all community subcategories, and services
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Users, GraduationCap, Search, HeartHandshake, Gift, 
  Megaphone, Car, HelpingHand, ArrowRight, MapPin, 
  Star, ChevronRight, Loader2, Sparkles, Phone, Eye
} from 'lucide-react';
import { useCountry } from '../../context/CountryContext';
import CommunityContactModal from '../community/CommunityContactModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Subcategory configuration
const SUBCATEGORIES = [
  { slug: 'ride-sharing', name: 'Ride Sharing', icon: Car, color: 'bg-green-500', gradient: 'from-green-500 to-emerald-600' },
  { slug: 'classes-workshops', name: 'Classes & Workshops', icon: GraduationCap, color: 'bg-purple-500', gradient: 'from-purple-500 to-pink-600' },
  { slug: 'lost-found', name: 'Lost & Found', icon: Search, color: 'bg-amber-500', gradient: 'from-amber-500 to-orange-600' },
  { slug: 'volunteers', name: 'Volunteers', icon: HeartHandshake, color: 'bg-rose-500', gradient: 'from-rose-500 to-red-600' },
  { slug: 'charity-donations', name: 'Charity & Donations', icon: Gift, color: 'bg-emerald-500', gradient: 'from-emerald-500 to-teal-600' },
  { slug: 'local-groups-clubs', name: 'Local Groups & Clubs', icon: Users, color: 'bg-blue-500', gradient: 'from-blue-500 to-indigo-600' },
  { slug: 'notices-announcements', name: 'Notices & Announcements', icon: Megaphone, color: 'bg-cyan-500', gradient: 'from-cyan-500 to-blue-600' },
  { slug: 'community-help', name: 'Community Help', icon: HelpingHand, color: 'bg-violet-500', gradient: 'from-violet-500 to-purple-600' }
];

// Subcategory Card
const SubcategoryCard = ({ subcategory, count, onClick }) => {
  const Icon = subcategory.icon;
  return (
    <Card 
      className="cursor-pointer hover:shadow-lg transition-all hover:-translate-y-1 border hover:border-blue-300"
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={`p-2.5 rounded-lg bg-gradient-to-br ${subcategory.gradient}`}>
            <Icon className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-sm truncate">{subcategory.name}</h3>
            <p className="text-xs text-gray-500">{count} ads</p>
          </div>
          <ChevronRight className="w-4 h-4 text-gray-400" />
        </div>
      </CardContent>
    </Card>
  );
};

// Listing Card for Community
const CommunityListingCard = ({ listing, country }) => {
  const navigate = useNavigate();
  const currencySymbol = country === 'UK' ? '£' : 'Rs.';
  const subcatConfig = SUBCATEGORIES.find(s => s.slug === listing.category) || SUBCATEGORIES[0];
  const Icon = subcatConfig.icon;

  return (
    <Card className="group hover:shadow-lg transition-all overflow-hidden">
      <div className="relative h-36">
        {listing.images?.[0] ? (
          <img 
            src={listing.images[0]} 
            alt={listing.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className={`w-full h-full bg-gradient-to-br ${subcatConfig.gradient} flex items-center justify-center`}>
            <Icon className="w-10 h-10 text-white/50" />
          </div>
        )}
        <Badge className={`absolute top-2 left-2 ${subcatConfig.color} text-white text-xs`}>
          {subcatConfig.name}
        </Badge>
        {listing.featured && (
          <Badge className="absolute top-2 right-2 bg-yellow-500 text-white text-xs">
            <Star className="w-3 h-3 mr-1" />
            Featured
          </Badge>
        )}
      </div>
      <CardContent className="p-3">
        <h4 className="font-semibold text-sm line-clamp-1 group-hover:text-blue-600">{listing.title}</h4>
        <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
          <MapPin className="w-3 h-3" />
          {listing.location}
          <span className="text-gray-300">•</span>
          <Eye className="w-3 h-3" />
          {listing.views || 0}
        </div>
        <div className="flex items-center justify-between mt-2">
          {listing.price > 0 ? (
            <span className="font-bold text-blue-600 text-sm">
              {currencySymbol}{listing.price?.toLocaleString()}
            </span>
          ) : (
            <Badge variant="outline" className="text-xs">Free</Badge>
          )}
          <div className="flex gap-1">
            <Button size="sm" variant="outline" className="h-7 text-xs px-2" onClick={() => navigate(`/listing/${listing.id}`)}>
              View
            </Button>
            <CommunityContactModal 
              post={listing} 
              country={country}
              trigger={
                <Button size="sm" variant="ghost" className="h-7 px-2">
                  <Phone className="w-3 h-3" />
                </Button>
              }
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Service Provider Card
const ServiceProviderCard = ({ provider }) => {
  const subcatConfig = SUBCATEGORIES.find(s => s.slug === provider.service_type) || SUBCATEGORIES[0];
  const Icon = subcatConfig.icon;

  return (
    <Card className="hover:shadow-md transition-all">
      <CardContent className="p-3">
        <div className="flex gap-3">
          <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0">
            {provider.images?.[0] ? (
              <img src={provider.images[0]} alt={provider.provider_name} className="w-full h-full object-cover" />
            ) : (
              <div className={`w-full h-full bg-gradient-to-br ${subcatConfig.gradient} flex items-center justify-center`}>
                <Icon className="w-6 h-6 text-white/70" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1">
              <h4 className="font-semibold text-sm truncate">{provider.provider_name}</h4>
              {provider.verified && (
                <Badge className="bg-green-500 text-white text-xs px-1">✓</Badge>
              )}
            </div>
            <p className="text-xs text-gray-500 line-clamp-1">{provider.description}</p>
            <div className="flex items-center gap-2 mt-1">
              {provider.rating > 0 && (
                <span className="flex items-center text-xs">
                  <Star className="w-3 h-3 fill-yellow-400 text-yellow-400 mr-0.5" />
                  {provider.rating.toFixed(1)}
                </span>
              )}
              <span className="text-xs text-gray-400">{provider.location}</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Community Hub Banner
const CommunityHubBanner = ({ onClick }) => (
  <Card className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white overflow-hidden">
    <CardContent className="p-6 relative">
      <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
      <div className="relative flex items-center justify-between">
        <div>
          <Badge className="bg-white/20 text-white mb-3">
            <Sparkles className="w-3 h-3 mr-1" />
            Community Hub
          </Badge>
          <h3 className="text-2xl font-bold mb-2">Explore Your Local Community Hub</h3>
          <p className="text-blue-100 max-w-lg">
            Connect with neighbors, share rides, learn new skills, volunteer, and more. 
            All in one place!
          </p>
        </div>
        <Button 
          size="lg" 
          className="bg-white text-blue-700 hover:bg-blue-50 shadow-lg"
          onClick={onClick}
        >
          Visit Hub
          <ArrowRight className="w-5 h-5 ml-2" />
        </Button>
      </div>
    </CardContent>
  </Card>
);

// Main Component
export default function CommunityLocalLifeSection() {
  const navigate = useNavigate();
  const { country } = useCountry();
  const [loading, setLoading] = useState(true);
  const [subcategoryCounts, setSubcategoryCounts] = useState({});
  const [allListings, setAllListings] = useState([]);
  const [providers, setProviders] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch listings from all subcategories
        const listingsPromises = SUBCATEGORIES.map(sub =>
          fetch(`${API_URL}/api/listings/?category=${sub.slug}&limit=4&country_code=${country}`)
            .then(r => r.json())
            .catch(() => ({ listings: [], total: 0 }))
        );

        const results = await Promise.all(listingsPromises);
        
        // Build counts and collect listings
        const counts = {};
        const listings = [];
        results.forEach((result, idx) => {
          counts[SUBCATEGORIES[idx].slug] = result?.total || 0;
          if (result?.listings) {
            listings.push(...result.listings.map(l => ({...l, category: SUBCATEGORIES[idx].slug})));
          }
        });
        
        setSubcategoryCounts(counts);
        setAllListings(listings.slice(0, 12)); // Limit to 12 total

        // Fetch service providers
        const providersResponse = await fetch(`${API_URL}/api/community/providers?country=${country}&limit=8`);
        const providersData = await providersResponse.json();
        setProviders(providersData?.providers || []);

      } catch (error) {
        console.error('Error fetching community data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [country]);

  if (loading) {
    return (
      <div className="py-12 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  const totalAds = Object.values(subcategoryCounts).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-8" data-testid="community-local-life-section">
      {/* Subcategories Grid */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold">Community Categories</h2>
            <p className="text-sm text-gray-500">{totalAds} total ads across all categories</p>
          </div>
          <Button variant="outline" onClick={() => navigate('/community')}>
            <Sparkles className="w-4 h-4 mr-2" />
            Visit Community Hub
          </Button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {SUBCATEGORIES.map(sub => (
            <SubcategoryCard
              key={sub.slug}
              subcategory={sub}
              count={subcategoryCounts[sub.slug] || 0}
              onClick={() => navigate(`/category/${sub.slug}`)}
            />
          ))}
        </div>
      </div>

      {/* Featured Listings */}
      {allListings.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold">Featured Community Listings</h2>
              <p className="text-sm text-gray-500">Latest from your community</p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {allListings.slice(0, 6).map(listing => (
              <CommunityListingCard 
                key={listing.id} 
                listing={listing} 
                country={country}
              />
            ))}
          </div>
        </div>
      )}

      {/* Service Providers */}
      {providers.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold">Community Service Providers</h2>
              <p className="text-sm text-gray-500">Local services for your needs</p>
            </div>
            <Button variant="link" onClick={() => navigate('/community')}>
              View all
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {providers.slice(0, 4).map(provider => (
              <ServiceProviderCard key={provider.id} provider={provider} />
            ))}
          </div>
        </div>
      )}

      {/* Community Hub Banner */}
      <CommunityHubBanner onClick={() => navigate('/community')} />
    </div>
  );
}
